import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSearchMerchant, SearchMerchantQueryQuery } from "@njoy/controller";
import SearchInput from "../../components/uiMerge/searchInput";
import SearchIcon from "../../assets/wallet/search-icon.png";
import { TemplateSub } from "../../components/layouts/TemplateSub";
import * as ROUTES from "../../constants/routes";
import AntAvatar from "../shared/AntAvatar";
import HouseIcon from "../../assets/wallet/house-icon.png";
import ViewBrandsSkeleton from "./viewBrandsSkeleton";
import useOnScreen from "../../hooks/useOnScreen";
import curatedOffers from "../../assets/discovery/merchant_banner.png";

type Merchant = NonNullable<
  NonNullable<SearchMerchantQueryQuery["searchMerchant"]>["data"][number]
>;
type MerchantData = Array<Merchant | null> | null | undefined;
const PAGE_SIZE = 10;
const INITIAL_SKELETON_COUNT = 6;
const LOAD_MORE_SKELETON_COUNT = 2;

const renderSkeletons = (count: number, keyPrefix: string) =>
  Array.from({ length: count }, (_, index) => (
    <ViewBrandsSkeleton key={`${keyPrefix}-${index}`} />
  ));

const ViewBrandsPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState("");
  const searchText = search.trim();
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  const isLoadMoreVisible = useOnScreen(loadMoreRef, "120px");
  const previousSearchTextRef = React.useRef(searchText);
  const [lastRequestedCount, setLastRequestedCount] = React.useState(0);
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  const { data, loading, loadMore, hasMoreMerchants } = useSearchMerchant({
    page: 1,
    pageSize: PAGE_SIZE,
    searchText: searchText || undefined,
  });

  const merchantResults = (data as MerchantData) ?? [];
  const merchants: Merchant[] = merchantResults.filter(
    (merchant): merchant is Merchant => Boolean(merchant),
  );
  const hasSearchText = searchText.length > 0;

  React.useEffect(() => {
    if (previousSearchTextRef.current === searchText) {
      return;
    }

    previousSearchTextRef.current = searchText;
    setIsInitialLoading(true);
    setIsLoadingMore(false);
    setLastRequestedCount(0);
  }, [searchText]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (isInitialLoading) {
      setIsInitialLoading(false);
    }

    if (isLoadingMore) {
      setIsLoadingMore(false);
    }
  }, [isInitialLoading, isLoadingMore, loading]);

  React.useEffect(() => {
    if (
      !isLoadMoreVisible ||
      !hasMoreMerchants ||
      isInitialLoading ||
      isLoadingMore ||
      loading ||
      merchants.length === 0
    ) {
      return;
    }

    if (merchants.length === lastRequestedCount) {
      return;
    }

    setLastRequestedCount(merchants.length);
    setIsLoadingMore(true);
    loadMore();
  }, [
    hasMoreMerchants,
    isInitialLoading,
    isLoadMoreVisible,
    isLoadingMore,
    lastRequestedCount,
    loadMore,
    loading,
    merchants.length,
  ]);

  return (
    <TemplateSub isCentered page="All Brands">
      <div className="w-full flex-1 h-full overflow-y-auto pb-4">
        <div className="w-full flex-1 overflow-y-auto pb-7">
          <Link to={ROUTES.FIND_OFFERS} className="w-full h-full ">
            <div className="w-full h-full mx-auto mb-2 items-center justify-center px-[1rem]  mt-1 min-w-0">
              <img
                className="w-full h-full max-h-[97px] object-fill rounded-[7px]"
                src={curatedOffers}
                alt="brand-banner"
              />
            </div>
          </Link>
          <div className="w-full px-[1rem] mt-7">
            <SearchInput
              prefixIcon={
                <img src={SearchIcon} alt="search" width={20} height={20} />
              }
              id="search"
              name="search"
              type="search"
              placeholder=" Name, category or username"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className=" px-5 flex justify-between items-center gap-5 mt-0"
              end2end={false}
            />
          </div>

          {isInitialLoading && (
            <div className="px-[1rem] mt-3 gap-3 flex flex-col">
              {renderSkeletons(INITIAL_SKELETON_COUNT, "initial-skeleton")}
            </div>
          )}

          {!isInitialLoading && (
            <>
              {hasSearchText && (
                <div className="px-[1rem] mt-8">
                  <p className="text-white text-[20px] text-left">
                    Search Results
                  </p>
                </div>
              )}

              {merchants.length > 0 && (
                <div
                  className={`flex flex-col gap-[10px] px-[1rem] ${
                    hasSearchText ? "mt-3" : "mt-7"
                  }`}
                >
                  {merchants.map((merchant: Merchant) => (
                    <div
                      key={merchant.merchantId}
                      onClick={() =>
                        navigate(
                          `${ROUTES.VIEW_DISCOVER_MERCHANT}/${merchant.merchantUserId}`,
                        )
                      }
                      className="flex w-full mt-1 items-center border-[#1d1d1d] justify-between gap-3 rounded-[10px] px-4 py-2 bg-[#1D1D1D] cursor-pointer"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-[54px] h-[54px] rounded-full overflow-hidden">
                          {merchant.logoUrl ? (
                            <img
                              src={merchant.logoUrl}
                              alt={merchant.companyName || "merchant logo"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <AntAvatar
                              firstname={merchant.companyName || ""}
                              height="54px"
                              width="54px"
                              fontSize="20px"
                              color="#D9D9D9"
                            />
                          )}
                        </div>

                        <div className="flex flex-col gap-1">
                          <p className="text-[16px] text-white font-semibold">
                            {merchant.companyName}
                          </p>

                          <p className="text-[12px] text-[#777]">
                            @{merchant.username}
                          </p>
                        </div>
                      </div>

                      <img src={HouseIcon} alt="" width={15} height={15} />
                    </div>
                  ))}

                  {isLoadingMore && (
                    <div className="gap-3 flex flex-col">
                      {renderSkeletons(
                        LOAD_MORE_SKELETON_COUNT,
                        "load-more-skeleton",
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {!isInitialLoading && hasSearchText && merchants.length === 0 && (
            <div className="px-[1rem] mt-8">
              <div className="flex items-center justify-center px-4 py-6 bg-[#1E1E1E] rounded-lg">
                <p className="text-[#B2B2B2] text-[14px]">
                  No brands matched your search.
                </p>
              </div>
            </div>
          )}

          <div ref={loadMoreRef} className="h-6 w-full" />
        </div>
      </div>
    </TemplateSub>
  );
};

export default ViewBrandsPage;
